import { useSelector } from 'react-redux';
import type SinatraState from '../types/SinatraState';
import { selectUserPermissions } from './selectUser';

interface StateWithSinatra {
  sinatra: SinatraState;
}

type SinatraUser = StateWithSinatra['sinatra']['user'];

/**
 * Hook to get the current user.
 */
export function useCurrentUser(): SinatraUser | undefined {
  const user = useSelector((state: StateWithSinatra) => state.sinatra.user);
  return user;
}

/**
 * Hook to get the current user's org.
 * @example
 * ```typescript
 * const org = useCurrentOrgName();
 * if (!org || !org.name) {
 *   throw new Error("Current org not available.");
 * }
 * headers.set('X-RapidSOS-Org', org.name);
 * ```
 */
export function useCurrentOrg(): SinatraUser['currentOrg'] {
  const org = useSelector(
    (state: StateWithSinatra) => state.sinatra.user.currentOrg,
  );
  return org;
}

/**
 * Hook to get the current user's org name.
 *
 * @example
 * ```typescript
 * const name = useCurrentOrgName();
 * if (!name) {
 *   throw new Error("Current org not available.");
 * }
 * headers.set('X-RapidSOS-Org', name);
 * ```
 */
export function useCurrentOrgName(): string | undefined {
  const org = useCurrentOrg();
  return org?.name;
}

/**
 * Hook to get the current user's permissions for their active role.
 */
export function useUserPermissions(): string[] {
  return useSelector(selectUserPermissions);
}
